// apps/provider/src/services/api/classes.js
// Class definitions, sessions, enrollments, and memberships.
//
// Methods: getClassDefinitions, getClassDefinition, createClassDefinition,
//          updateClassDefinition, deleteClassDefinition,
//          getClassSessions, getClassSession, updateClassSession,
//          enrollPatient, cancelEnrollment, getEnrollments,
//          bulkCheckIn, finalizeAttendance,
//          getClassMemberships, createClassMembership, cancelClassMembership
//
// Response types: @prism-ehr/shared → ClassDefinitionResponse, ClassSessionResponse,
//                 ClassSessionDetailResponse, ClassEnrollmentResponse,
//                 ClassMembershipResponse

/**
 * @returns {Object} Classes domain methods (bound to `this` of the composed apiService)
 */
export function createClassesMethods() {
  return {

    // ============ Class Definitions ============

    /**
     * List class definitions for the organization.
     *
     * Xano: GET /class_definition
     *
     * @param {Object} filters - { organization_id, location_id?, provider_id?, is_active? }
     * @returns {Promise<import('@prism-ehr/shared').ClassDefinitionResponse[]>}
     */
    getClassDefinitions(filters = {}) {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, val]) => {
        if (val !== undefined && val !== null) params.append(key, val);
      });
      return this.request(`/class_definition?${params.toString()}`, { method: 'GET' });
    },

    /**
     * Get a single class definition by ID.
     *
     * Xano: GET /class_definition/{class_definition_id}
     *
     * @param {string} id
     * @returns {Promise<import('@prism-ehr/shared').ClassDefinitionResponse>}
     */
    getClassDefinition(id) {
      return this.request(`/class_definition/${id}`, { method: 'GET' });
    },

    /**
     * Create a new class definition.
     *
     * Xano: POST /class_definition
     *
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared').ClassDefinitionResponse>}
     */
    createClassDefinition(data) {
      return this.request('/class_definition', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Update a class definition.
     *
     * Xano: PATCH /class_definition/{class_definition_id}
     *
     * @param {string} id
     * @param {Object} data - definition fields; `apply_to_upcoming: true` also re-times /
     *   reconciles future scheduled sessions when start_time, duration or days changed
     *   (the Edit page sets it after its confirm). Without it, only the definition changes.
     * @returns {Promise<import('@prism-ehr/shared').ClassDefinitionResponse>}
     */
    updateClassDefinition(id, data) {
      return this.request(`/class_definition/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Delete (soft-delete) a class definition.
     *
     * Xano: DELETE /class_definition/{class_definition_id}
     *
     * @param {string} id
     * @returns {Promise<void>}
     */
    deleteClassDefinition(id) {
      return this.request(`/class_definition/${id}`, { method: 'DELETE' });
    },

    // ============ Class Sessions ============

    /**
     * List class sessions (used for calendar integration).
     *
     * Xano: GET /class_session
     *
     * @param {Object} filters - { organization_id, start_date?, end_date?, provider_id?, location_id?, status?, class_definition_id? }
     * @returns {Promise<import('@prism-ehr/shared').ClassSessionResponse[]>}
     */
    getClassSessions(filters = {}) {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, val]) => {
        if (val !== undefined && val !== null) params.append(key, val);
      });
      return this.request(`/class_session?${params.toString()}`, { method: 'GET' });
    },

    /**
     * Get a single class session with enrollment list.
     *
     * Xano: GET /class_session/{class_session_id}
     *
     * @param {string} id
     * @returns {Promise<import('@prism-ehr/shared').ClassSessionDetailResponse>}
     */
    getClassSession(id) {
      return this.request(`/class_session/${id}`, { method: 'GET' });
    },

    /**
     * Update a class session (override provider, capacity, cancel, notes).
     *
     * Xano: PATCH /class_session/{class_session_id}
     *
     * @param {string} id
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared').ClassSessionResponse>}
     */
    updateClassSession(id, data) {
      return this.request(`/class_session/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    // ============ Enrollments ============

    /**
     * Enroll a patient in a class session.
     *
     * Xano: POST /class_enrollment
     *
     * @param {Object} data - { class_session_id, patient_id, payment_method, enrollment_source, patient_package_id?, payment_plan_id?, create_invoice? }
     *   create_invoice (default true): single purchase + seated + priced -> a class invoice is created and returned as `invoice_id`.
     * @returns {Promise<import('@prism-ehr/shared').ClassEnrollmentResponse>}
     */
    enrollPatient(data) {
      return this.request('/class_enrollment', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Cancel an enrollment.
     *
     * Xano: POST /class_enrollment/{class_enrollment_id}/cancel
     *
     * @param {string} enrollmentId
     * @returns {Promise<import('@prism-ehr/shared').ClassEnrollmentResponse>}
     */
    cancelEnrollment(enrollmentId) {
      return this.request(`/class_enrollment/${enrollmentId}/cancel`, { method: 'POST' });
    },

    /**
     * List enrollments for a session or patient.
     *
     * Xano: GET /class_enrollment
     *
     * @param {Object} filters - { class_session_id?, patient_id? }
     * @returns {Promise<import('@prism-ehr/shared').ClassEnrollmentResponse[]>}
     */
    getEnrollments(filters = {}) {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, val]) => {
        if (val !== undefined && val !== null) params.append(key, val);
      });
      return this.request(`/class_enrollment?${params.toString()}`, { method: 'GET' });
    },

    /**
     * Bulk check-in patients for a class session.
     *
     * Xano: POST /class_session/{class_session_id}/check-in
     *
     * @param {string} sessionId
     * @param {string[]} enrollmentIds
     * @returns {Promise<Object>}
     */
    bulkCheckIn(sessionId, enrollmentIds) {
      return this.request(`/class_session/${sessionId}/check-in`, {
        method: 'POST',
        body: JSON.stringify({ enrollment_ids: enrollmentIds })
      });
    },

    /**
     * Batch-create class invoices for selected enrollments of one session (idempotent).
     * Drop-in rows get their own invoice; series members get the membership's series
     * invoice when it has none. Already invoiced / free / package / waitlisted rows are skipped.
     *
     * Xano: POST /class_session/{class_session_id}/create-invoices
     *
     * @param {string} sessionId
     * @param {string[]} enrollmentIds
     * @returns {Promise<{created: {enrollment_id: string, invoice_id: string, kind: 'drop_in'|'series'}[], skipped: {enrollment_id: string, reason: string}[]}>}
     */
    createSessionInvoices(sessionId, enrollmentIds) {
      return this.request(`/class_session/${sessionId}/create-invoices`, {
        method: 'POST',
        body: JSON.stringify({ enrollment_ids: enrollmentIds })
      });
    },

    /**
     * Finalize attendance for a completed class session.
     *
     * Xano: POST /class_session/{class_session_id}/attendance
     *
     * @param {string} sessionId
     * @param {Object} data - { attended_ids: string[], no_show_ids: string[] }
     * @returns {Promise<Object>}
     */
    finalizeAttendance(sessionId, data) {
      return this.request(`/class_session/${sessionId}/attendance`, {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    // ============ Memberships ============

    /**
     * List class memberships.
     *
     * Xano: GET /class_membership
     *
     * @param {Object} filters - { class_definition_id?, patient_id? }
     * @returns {Promise<import('@prism-ehr/shared').ClassMembershipResponse[]>}
     */
    getClassMemberships(filters = {}) {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, val]) => {
        if (val !== undefined && val !== null) params.append(key, val);
      });
      return this.request(`/class_membership?${params.toString()}`, { method: 'GET' });
    },

    /**
     * Create a recurring class membership.
     *
     * Xano: POST /class_membership
     *
     * @param {Object} data - { class_definition_id, patient_id, payment_method, patient_package_id?, payment_plan_id?, create_invoice? }
     *   Provider path: patient_id required. Backfills the patient into every upcoming scheduled session;
     *   single purchase + series_price -> ONE series invoice (create_invoice default true).
     * @returns {Promise<{membership: import('@prism-ehr/shared').ClassMembershipResponse, enrollments_created: number}>}
     */
    createClassMembership(data) {
      return this.request('/class_membership', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Cancel a class membership.
     *
     * Xano: POST /class_membership/{class_membership_id}/cancel
     *
     * @param {string} membershipId
     * @returns {Promise<import('@prism-ehr/shared').ClassMembershipResponse>}
     */
    cancelClassMembership(membershipId) {
      return this.request(`/class_membership/${membershipId}/cancel`, { method: 'POST' });
    },
  };
}
